import { CharacterDice, RollType } from "../../../../../entities/rolls/Roll";
import { CharacterClearRollSkill, CharacterFilterItems, CharacterHideItemAdder, CharacterResetHand, CharacterSetCommunalRolls, CharacterSetEntityPending, CharacterSetHand, CharacterSetPreviewItemInstanceId, CharacterSetPreviewSlotId, CharacterSetRolling, CharacterSetRollSkill, CharacterShowItemAdder } from "../actions/CharacterSpecificActions";
import { CharacterState } from "../CharacterStore.State";
import { CharacterReducerHelpers } from "./CharacterReducerHelpers";

const setEntityPending = (state: CharacterState, action: CharacterSetEntityPending): CharacterState => {
    const { characterId, entityId, pending } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const pendingProperties = { ...character.pendingProperties };

    if (pending) {
        pendingProperties[entityId] = true;
    }
    else {
        delete pendingProperties[entityId];
    }

    const newCharacter = { ...character, pendingProperties };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const previewItem = (state: CharacterState, action: CharacterSetPreviewItemInstanceId): CharacterState => {
    const { characterId, previewItemInstanceId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, previewItemInstanceId };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
};

const previewSlot = (state: CharacterState, action: CharacterSetPreviewSlotId): CharacterState => {
    const { characterId, previewSlotId, previewSlotItemInstanceId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, previewSlotId, previewSlotItemInstanceId };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const showItemAdder = (state: CharacterState, action: CharacterShowItemAdder): CharacterState => {
    const { characterId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const newCharacter = { ...character, showItemAdder: true };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const hideItemAdder = (state: CharacterState, action: CharacterHideItemAdder): CharacterState => {
    const { characterId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const newCharacter = { ...character, showItemAdder: false };
    newCharacter.showItemAdder = false;

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
};

const filterItems = (state: CharacterState, action: CharacterFilterItems): CharacterState => {
    const { characterId, filter } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, itemFilter: { ...filter } };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setHand = (state: CharacterState, action: CharacterSetHand): CharacterState => {
    const { characterId, hand } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const newCharacter = {
        ...character,
        currentDice: {
            ...character.currentDice,
            actionDice: hand.actionDice,
            extraDice: hand.extraDice
        }
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setRolling = (state: CharacterState, action: CharacterSetRolling): CharacterState => {
    const { characterId, rollRequest } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, rollRequest };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const resetHand = (state: CharacterState, action: CharacterResetHand): CharacterState => {
    const { characterId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const currentDice: CharacterDice = {
        rollType: RollType.Normal,
        actionDice: [],
        extraDice: [],
    };
    const newCharacter = {
        ...character,
        currentDice
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setCommunalDice = (state: CharacterState, action: CharacterSetCommunalRolls): CharacterState => {
    const { characterId, communalDice } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, communalDice };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setRollSkill = (state: CharacterState, action: CharacterSetRollSkill): CharacterState => {
    const { characterId, skillId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = {
        ...character,
        currentDice: {
            ...character.currentDice,
            skillId
        }
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const clearRollSkill = (state: CharacterState, action: CharacterClearRollSkill): CharacterState => {
    const { characterId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = {
        ...character,
        currentDice: {
            ...character.currentDice,
            skillId: undefined
        }
    };
    
    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

export const CharacterSpecificReducers = {
    setEntityPending,
    previewItem,
    previewSlot,
    showItemAdder,
    hideItemAdder,
    filterItems,
    setHand,
    setRolling,
    resetHand,
    setCommunalDice,
    setRollSkill,
    clearRollSkill,
}